// src/app/shared/services/csv-export.service.ts
import { Injectable } from '@angular/core';
import { CsvAccessor, CsvColumn, CsvOptions } from '../models/csv-export.model';

@Injectable({ providedIn: 'root' })
export class CsvExportService {
  /**
   * Exports the provided data set to a downloadable CSV file.
   *
   * @typeParam T - Data model type of each row
   *
   * @param data - Array of data rows to export
   * @param columns - Column definitions describing CSV structure
   * @param options - Optional CSV formatting and file options
   */
  exportToCsv<T extends Record<string, any>>(
    data: T[],
    columns: CsvColumn<T>[],
    options: CsvOptions = {},
  ): void {
    const {
      fileName = 'export.csv',
      delimiter = ',',
      includeBOM = true,
      newline = '\r\n',
      stringifyObjects = true,
    } = options;

    if (!data?.length) {
      console.warn('[CsvExportService] No data provided, skipping export.');
      return;
    }

    if (!columns?.length) {
      throw new Error(
        '[CsvExportService] Columns must be provided for CSV export.',
      );
    }

    const headerRow = columns
      .map((c) => this.escapeCell(c.header, delimiter))
      .join(delimiter);

    const bodyRows = data.map((row) =>
      columns
        .map((col) => {
          const rawValue = this.getValue(row, col.accessor);

          // If a formatter exists, it owns the final string.
          // Otherwise, we normalize primitives and optionally stringify objects.
          const cellText = col.format
            ? col.format(rawValue, row)
            : this.normalizeValue(rawValue, stringifyObjects);

          return this.escapeCell(cellText, delimiter);
        })
        .join(delimiter),
    );

    const csv = [headerRow, ...bodyRows].join(newline);
    const csvWithBom = includeBOM ? '\uFEFF' + csv : csv;

    this.downloadTextFile(csvWithBom, fileName, 'text/csv;charset=utf-8;');
  }

  /**
   * Resolves a value from a row using the provided accessor.
   */
  private getValue<T>(row: T, accessor: CsvAccessor<T>): unknown {
    if (typeof accessor === 'function') {
      return accessor(row);
    }

    if (typeof accessor !== 'string') {
      return (row as any)[accessor];
    }

    return this.getByPath(row as any, accessor);
  }

  /**
   * Safely resolves a nested value using a string path.
   *
   * Supports:
   * - Dot notation: "user.profile.name"
   * - Array access: "items[0].price"
   */
  private getByPath(obj: any, path: string): unknown {
    if (!obj || !path) return undefined;

    const parts = path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .filter(Boolean);

    let current = obj;
    for (const key of parts) {
      if (current == null) return undefined;
      current = current[key];
    }

    return current;
  }

  /**
   * Normalizes a raw value into a string when no formatter is provided.
   */
  private normalizeValue(value: unknown, stringifyObjects: boolean): string {
    if (value === null || value === undefined) return '';

    if (typeof value === 'object') {
      return stringifyObjects ? JSON.stringify(value) : '';
    }

    return String(value);
  }

  /**
   * Escapes a CSV cell according to RFC 4180 rules.
   */
  private escapeCell(value: string, delimiter: string): string {
    const str = value ?? '';

    const mustQuote =
      str.includes(delimiter) ||
      str.includes('"') ||
      str.includes('\n') ||
      str.includes('\r');

    if (!mustQuote) return str;

    return `"${str.replace(/"/g, '""')}"`;
  }

  /**
   * Triggers a browser download for the generated CSV content.
   */
  private downloadTextFile(
    content: string,
    fileName: string,
    mime: string,
  ): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    URL.revokeObjectURL(url);
  }
}
